import { VisualRandom } from '../core/VisualRandom.js';
import { MUTATION_ACTIONS } from '../core/VisualMutation.js';
import { BLEND_MODES } from '../core/VisualRenderer.js';
import { VISUAL_RECIPE_VERSION, layer, type VisualRecipe } from '../recipe/VisualRecipe.js';
import type { SignalMapping } from '../layers/VisualLayer.js';

/**
 * Builds a whole recipe out of a seed.
 *
 * The requirement that makes this worth having is reproducibility: the same
 * seed and the same available renderers must produce the same recipe, on any
 * machine, on any day. So every choice below — how many layers, which renderer,
 * what it is mapped to — is drawn from one `VisualRandom` in a fixed order.
 * Reordering the draws changes every seed's output, which is why the sequence
 * here is deliberately linear and boring.
 *
 * `Math.random()` appears nowhere. That is the whole point.
 */

/** Signals worth mapping. Not every signal makes an interesting driver. */
const EVENT_SIGNALS = ['NOTE_HIT', 'NOTE_MISS', 'BEAT', 'BAR', 'ONSET', 'PLAYER_INPUT'] as const;
const LEVEL_SIGNALS = ['COMBO', 'BASS', 'TREBLE', 'AMPLITUDE', 'ACCURACY'] as const;

/** Actions general enough that any renderer might answer them. */
const COMMON_ACTIONS = MUTATION_ACTIONS.filter((action) =>
  ['SPAWN', 'EXPAND', 'CONTRACT', 'ROTATE', 'SHIFT', 'DISTORT', 'SPLASH', 'TURBULENCE', 'DENSITY'].includes(
    action,
  ),
);

export interface RandomRecipeOptions {
  readonly seed: number;
  /** Renderer types this build actually has. */
  readonly available: readonly string[];
  readonly minLayers?: number;
  readonly maxLayers?: number;
  /**
   * Kept in every generated recipe as the ground layer.
   *
   * Without it a random draw can produce a stack with no world underneath it,
   * which reads as a bug rather than as a composition.
   */
  readonly anchorRenderer?: string;
}

export function randomRecipe(options: RandomRecipeOptions): VisualRecipe {
  const random = new VisualRandom(options.seed);
  const anchor = options.anchorRenderer ?? 'MANDALA';
  const pool = options.available.filter((type) => type !== anchor);
  const min = Math.max(1, options.minLayers ?? 2);
  const max = Math.max(min, options.maxLayers ?? 4);

  const count = random.int(min, max + 1);
  const layers = [];

  if (options.available.includes(anchor)) {
    layers.push(
      layer({
        id: 'mandala',
        renderer: anchor,
        zDepth: 0,
        seed: random.int(0, 1_000_000),
        opacity: 1,
        blendMode: 'normal',
        intensity: random.range(0.85, 1.25),
        signalMapping: randomMapping(random, 1),
      }),
    );
  }

  for (let index = 0; index < count && pool.length > 0; index += 1) {
    const renderer = random.pick(pool);
    layers.push(
      layer({
        id: `${renderer.toLowerCase()}-${index + 1}`,
        renderer,
        zDepth: index + 1,
        seed: random.int(0, 1_000_000),
        opacity: round(random.range(0.35, 1)),
        blendMode: random.pick(BLEND_MODES),
        intensity: round(random.range(0.5, 1.8)),
        audioReactivity: round(random.range(0.5, 1.5)),
        parameters: randomParameters(random, renderer),
        signalMapping: randomMapping(random, random.int(1, 4)),
      }),
    );
  }

  return { version: VISUAL_RECIPE_VERSION, seed: options.seed, renderMode: 'RANDOM', layers };
}

function randomMapping(random: VisualRandom, count: number): SignalMapping {
  const mapping: Record<string, unknown> = {};
  for (let index = 0; index < count; index += 1) {
    // Events get one-shot actions; levels get sustained ones with a scale.
    if (random.bool(0.6)) {
      const signal = random.pick(EVENT_SIGNALS);
      mapping[signal] = {
        action: random.pick(COMMON_ACTIONS),
        strength: round(random.range(0.4, 1)),
      };
    } else {
      const signal = random.pick(LEVEL_SIGNALS);
      mapping[signal] = {
        action: random.pick(COMMON_ACTIONS),
        scale: round(random.range(0.2, 1.2)),
        ...(random.bool(0.4) ? { curve: 'sqrt' as const } : {}),
      };
    }
  }
  return mapping as SignalMapping;
}

function randomParameters(
  random: VisualRandom,
  renderer: string,
): Record<string, number | string | boolean> {
  if (renderer === 'RIPPLE') {
    return {
      spawnRate: round(random.range(0.5, 4)),
      growth: round(random.range(0.25, 0.9)),
      lifetime: round(random.range(0.9, 3)),
      distortion: round(random.range(0, 0.6)),
      hue: round(random.next()),
      segments: random.int(24, 128),
    };
  }
  if (renderer === 'MATH' || renderer === 'CODE') {
    return {
      scale: round(random.range(0.4, 0.75)),
      offsetX: round(random.range(-0.35, 0.05)),
      offsetY: round(random.range(-0.7, 0.7)),
      hue: random.int(120, 220),
    };
  }
  return {};
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
